import { defineComponent, reactive, readonly, SetupContext, ref, toRefs, watch } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { informationTabsType, newsTabsType, informationListType, newsListType } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                                   // Windowサイズ
    informationTabs: informationTabsType[];                                                 // お知らせタブリスト
    newsTabs: newsTabsType[];                                                               // 新着情報タブリスト
    infoList: informationListType[];                                                        // お知らせ一覧
    allInfoList: informationListType[];                                                     // お知らせ一覧(全データ)
    newsList: newsListType[];                                                               // 新着情報一覧
    allNewsList: newsListType[];                                                            // 新着情報一覧(全データ)
    isInfoLoading: number;                                                                  // お知らせ一覧テキスト表示フラグ 初期表示またはデータあり(文字表示なし)：0 ロード中：1 データがない場合：2
    isNewsLoading: number;                                                                  // 新着情報一覧テキスト表示フラグ 初期表示またはデータあり(文字表示なし)：0 ロード中：1 データがない場合：2
    isDarkTheme: boolean;                                                                   // ダークテーマ使用フラグ
    isSystemAdminMode: boolean;                                                             // システム管理モードフラグ true：システム管理モード false：ノーマルモード
    prefecturalSchoolOrgTypeId: string[];                                                   // 県立学校の組織型ID
    municipalSchoolOrgTypeId: string[];                                                     // 市町村立学校の組織型ID
};

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                                           // ルータ
        const simpleTableHeight = 185;                                                      // テーブルサイズの基準設定
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                            // テーブルのデータ取得中に表示するテキスト
        const tableNoDataText = SystemConst.TABLE_NO_DATA_TEXT;                             // テーブルのデータがないときに表示するテキスト

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                                     // ウィンドウサイズ
            isInfoLoading: SystemConst.TABLE_TEXT['DEFAULT'],                               // お知らせ一覧テキスト表示フラグ 初期表示またはデータあり(文字表示なし)：0 ロード中：1 データがない場合：2
            isNewsLoading: SystemConst.TABLE_TEXT['DEFAULT'],                               // 新着情報一覧テキスト表示フラグ 初期表示またはデータあり(文字表示なし)：0 ロード中：1 データがない場合：2
            isSystemAdminMode: false,                                                       // システム管理モードフラグ true：システム管理モード false：ノーマルモード
            informationTabs: [                                                              // お知らせタブリスト
                {
                    text: "すべて",
                    icon: "",
                    isDisplay: true
                },
                {
                    text: "県立学校向け",
                    icon: "",
                    isDisplay: true
                },
                {
                    text: "市町村立学校向け",
                    icon: "",
                    isDisplay: true
                }
            ],
            newsTabs: [                                                                     // 新着情報タブリスト
                {
                    text: "すべて",
                    icon: "",
                    isDisplay: true
                },
                {
                    text: "公文書",
                    icon: SystemConst.NEWS_ICON['OFFICIAL_DOCUMENT'],
                    isDisplay: true
                },
                {
                    text: "重要情報のお知らせ",
                    icon: SystemConst.NEWS_ICON['IMPORTANT_INFORMATION'],
                    isDisplay: true
                },
                {
                    text: "教材データベース",
                    icon: SystemConst.NEWS_ICON['MATERIAL_DB'],
                    isDisplay: true
                },
                {
                    text: "教育用コンテンツ",
                    icon: SystemConst.NEWS_ICON['CONTENT'],
                    isDisplay: true
                }
            ],
            infoList: [],                                                                   // お知らせデータ
            allInfoList: [],                                                                // お知らせ一覧(全データ)
            newsList: [],                                                                   // 新着情報データ
            allNewsList: [],                                                                // 新着情報一覧(全データ)
            isDarkTheme: ctx.root.$vuetify.theme.dark,                                      // ダークテーマ使用フラグ
            prefecturalSchoolOrgTypeId: [],                                                 // 県立学校の組織型ID
            municipalSchoolOrgTypeId: []                                                    // 市町村立学校の組織型ID
        });
        const commonRef = ref<any>(null);                                                   // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const infoTabsRef = ref<any>(null);                                                 // お知らせ一覧のタブ
        const newsTabsRef = ref<any>(null);                                                 // 新着情報一覧のタブ
        const isInformation = ctx.root.$store.state.authenticationUser.role.isInformation;  // お知らせ権限 true：お知らせ権限あり false：お知らせ権限なし

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(ctx.root.$store.state.commonLayout, () => {
            state.isDarkTheme = ctx.root.$store.state.commonLayout.isDarkTheme;
        })

        // システム管理モードフラグを監視し、変更があった場合ボタンの表示/非表示を行う
        // システム管理モードフラグとお知らせ権限(isInformation)がtrueだった場合、一覧ボタンと新規登録ボタンを表示する
        const { isSystemAdminMode } = toRefs(ctx.root.$store.state);
        watch(isSystemAdminMode, () => {
            state.isSystemAdminMode = ctx.root.$store.state.isSystemAdminMode;

            // お知らせ一覧と新着情報一覧を再取得・設定する
            getAndSetList();
        })

        /**
         * 初期表示
         */
        created();
        async function created() {

            // titleIconクリア
            ctx.root.$store.commit('setTitleIcon', '');

            // mainTitleクリア
            ctx.root.$store.commit('setMainTitle', '');

            // ローディング
            state.isInfoLoading = SystemConst.TABLE_TEXT['LOADING'];
            state.isNewsLoading = SystemConst.TABLE_TEXT['LOADING'];

            // storeのシステム管理モードフラグとお知らせ権限(isInformation)がtrueだった場合、一覧ボタンと新規登録ボタンを表示する
            state.isSystemAdminMode = ctx.root.$store.state.isSystemAdminMode;

            // お知らせ、新着一覧情報取得を取得する
            await getList();

            // お知らせ一覧、新着情報一覧を設定する
            setList();

            // ローディング削除
            // データがない場合、「データはありません。」と表示
            if (state.infoList.length === void 0 || state.infoList.length === 0) {
                state.isInfoLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isInfoLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }
            if (state.newsList === void 0 || state.newsList.length === 0) {
                state.isNewsLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isNewsLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }
        }

        /**
         * お知らせ一覧、新着情報一覧取得
         */
        async function getList() {

            // お知らせ、新着一覧情報取得APIを呼び出す
            const response = await ctx.root.$store.dispatch('home/getInformationNewsList');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // ローディング解除
                state.isInfoLoading = SystemConst.TABLE_TEXT['DEFAULT'];
                state.isNewsLoading = SystemConst.TABLE_TEXT['DEFAULT'];

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得したデータを格納する
            state.infoList = response.data.information;
            state.newsList = response.data.news;
            state.prefecturalSchoolOrgTypeId = response.data.prefecturalSchoolOrgTypeId;
            state.municipalSchoolOrgTypeId = response.data.municipalSchoolOrgTypeId;
        }

        /**
         * お知らせ一覧、新着情報一覧設定
         */
        function setList() {

            // お知らせ一覧の設定
            for (const key in state.infoList) {
                // 公開範囲を設定する
                if (state.infoList[key].severity === Number(SystemConst.ADD_SEVERITY['ADMINISTER'])) {
                    // 重要度が2(管理者)の場合、「管理者」を表示
                    state.infoList[key].publicationRangeText = SystemConst.PUBLICATION_RANGE_STR['ADMINISTER'];
                } else {
                    // 県立学校の組織型IDが含まれる場合、「県立」を表示
                    if ((state.prefecturalSchoolOrgTypeId.filter(i => state.infoList[key].organizationTypeId.indexOf(i) !== -1)).length) {
                        state.infoList[key].publicationRangeText = SystemConst.PUBLICATION_RANGE_STR['PUBLIC'];
                    }

                    // 市町村立学校の組織型IDが含まれる場合、「市町村立」を表示
                    if ((state.municipalSchoolOrgTypeId.filter(i => state.infoList[key].organizationTypeId.indexOf(i) !== -1)).length) {
                        // 既に公開範囲が設定されている場合、「・」を追加する
                        if (state.infoList[key].publicationRangeText !== void 0) {
                            state.infoList[key].publicationRangeText += '・' + SystemConst.PUBLICATION_RANGE_STR['MUNICIPAL'];
                        } else {
                            state.infoList[key].publicationRangeText = SystemConst.PUBLICATION_RANGE_STR['MUNICIPAL'];
                        }
                    }

                    // 公開範囲が3（所属組織の生徒を含む全アカウント）または4（所属組織の全教職員アカウント（一般を含む））の場合、「校内」を表示
                    if (state.infoList[key].publicationRange === SystemConst.PUBLICATION_RANGE['TEACHER_STUDENT'] ||
                        state.infoList[key].publicationRange === SystemConst.PUBLICATION_RANGE['TEACHER']) {
                        state.infoList[key].publicationRangeText = SystemConst.PUBLICATION_RANGE_STR['INSIDE'];
                    }

                    // それ以外の場合、「その他」を表示
                    if (state.infoList[key].publicationRangeText === void 0) {
                        state.infoList[key].publicationRangeText = SystemConst.PUBLICATION_RANGE_STR['OTHER'];
                    }
                }

                // 重要度(severity)から重要度アイコン(severityIcon)、重要度アイコンカラー(severityIconColor)を設定する
                // 1：mdi-alert-circle
                // 2:mdi-information
                if (state.infoList[key].severity === Number(SystemConst.ADD_SEVERITY['IMPORTANT'])) {
                    state.infoList[key].severityIcon = SystemConst.SEVERITY_ICON['MDL_ALERT'];
                    state.infoList[key].severityIconColor = SystemConst.SEVERITY_ICON_COLOR['ERROR'];
                } else if (state.infoList[key].severity === Number(SystemConst.ADD_SEVERITY['ADMINISTER'])) {
                    state.infoList[key].severityIcon = SystemConst.SEVERITY_ICON['MDL_INFORMATION'];
                    state.infoList[key].severityIconColor = SystemConst.SEVERITY_ICON_COLOR['PRIMARY'];
                }
            }

            // 新規情報一覧の設定
            for (const key in state.newsList) {
                // サービス区分(serviceDivision)からサービス区分表示(serviceDivisionText)とアイコン(icon)を設定する
                // サービス区分表示
                // official_document：公文書
                // important_information：重要情報のお知らせ
                // material_db：教材データベース
                // content：教育用コンテンツ

                // アイコン
                // official_document：mdi-file-document
                // important_information：重要情報のお知らせの定義
                // material_db：教材データベースの定義
                // content：教育用コンテンツの定義
                if (state.newsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['OFFICIAL_DOCUMENT']) {
                    state.newsList[key].serviceDivisionText = SystemConst.SERVICE_DIVISION_TEXT['OFFICIAL_DOCUMENT'];
                    state.newsList[key].icon = SystemConst.NEWS_ICON['OFFICIAL_DOCUMENT'];
                } else if (state.newsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['IMPORTANT_INFORMATION']) {
                    state.newsList[key].serviceDivisionText = SystemConst.SERVICE_DIVISION_TEXT['IMPORTANT_INFORMATION'];
                    state.newsList[key].icon = SystemConst.NEWS_ICON['IMPORTANT_INFORMATION'];
                } else if (state.newsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['MATERIAL_DB']) {
                    state.newsList[key].serviceDivisionText = SystemConst.SERVICE_DIVISION_TEXT['MATERIAL_DB'];
                    state.newsList[key].icon = SystemConst.NEWS_ICON['MATERIAL_DB'];
                } else if (state.newsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['CONTENT']) {
                    state.newsList[key].serviceDivisionText = SystemConst.SERVICE_DIVISION_TEXT['CONTENT'];
                    state.newsList[key].icon = SystemConst.NEWS_ICON['CONTENT'];
                }
            }

            // お知らせ一覧全データを保持
            state.allInfoList = state.infoList;

            // 新着情報一覧全データを保持
            state.allNewsList = state.newsList;
        }

        /**
         * お知らせ一覧タブ切り替え
         * @param selectTab タブ識別
         */
        function changeInfoTabs(selectTab: number) {

            // フィルタ後お知らせ一覧の初期化
            const tmpList = [];

            // 表示一覧の初期化
            state.infoList = [];

            // stateのinfoListを公開範囲に沿って格納する
            for (const key in state.allInfoList) {
                if (selectTab === SystemConst.INFO_TABS['PREFECTURAL']) {
                    // selectTabが1(県立学校向け)の場合、県立学校の組織型IDのデータをフィルタ
                    if ((state.prefecturalSchoolOrgTypeId.filter(i => state.allInfoList[key].organizationTypeId.indexOf(i) !== -1)).length) {
                        tmpList.push(state.allInfoList[key]);
                    }
                } else if (selectTab === SystemConst.INFO_TABS['MUNICIPAL']) {
                    // selectTabが2(市町村立学校向け)の場合 、市町村立学校の組織型IDのデータをフィルタ
                    if ((state.municipalSchoolOrgTypeId.filter(i => state.allInfoList[key].organizationTypeId.indexOf(i) !== -1)).length) {
                        tmpList.push(state.allInfoList[key]);
                    }
                } else {
                    // selectTabが0(全て)の場合、すべてのデータを表示
                    tmpList.push(state.allInfoList[key]);
                }
            }
            state.infoList = tmpList;

            // データがない場合、一覧に「データはありません。」と表示
            if (state.infoList.length === 0) {
                state.isInfoLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isInfoLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }
        }

        /**
         * 新着情報一覧タブ切り替え
         * @param selectTab タブ識別
         */
        function changeNewsTabs(selectTab: number) {

            // フィルタ後新着情報一覧の初期化
            const tmpList = [];

            // 表示一覧の初期化
            state.newsList = [];

            // stateのnewsListをサービス区分に沿って格納する
            for (const key in state.allNewsList) {
                // selectTabが0(全て)または1(公文書)の場合
                if (selectTab === SystemConst.NEWS_TABS['ALL'] || selectTab === SystemConst.NEWS_TABS['OFFICIAL_DOCUMENT']) {
                    // サービス区分がofficial_document(公文書)のデータをフィルタ
                    if (state.allNewsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['OFFICIAL_DOCUMENT']) {
                        tmpList.push(state.allNewsList[key]);
                    }
                }

                // selectTabが0(全て)または2(重要情報のお知らせ)の場合
                if (selectTab === SystemConst.NEWS_TABS['ALL'] || selectTab === SystemConst.NEWS_TABS['IMPORTANT_INFORMATION']) {
                    // サービス区分がimportant_information(重要情報のお知らせの定義)のデータをフィルタ
                    if (state.allNewsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['IMPORTANT_INFORMATION']) {
                        tmpList.push(state.allNewsList[key]);
                    }
                }

                // サービス区分が0(全て)または3(教材データベース)の場合
                if (selectTab === SystemConst.NEWS_TABS['ALL'] || selectTab === SystemConst.NEWS_TABS['MATERIAL_DB']) {
                    // サービス区分がmaterial_db(教材データベースの定義)のデータをフィルタ
                    if (state.allNewsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['MATERIAL_DB']) {
                        tmpList.push(state.allNewsList[key]);
                    }
                }

                // サービス区分が0(全て)または4(教育用コンテンツ)の場合
                if (selectTab === SystemConst.NEWS_TABS['ALL'] || selectTab === SystemConst.NEWS_TABS['CONTENT']) {
                    // サービス区分がcontent(教育用コンテンツの定義)のデータをフィルタ
                    if (state.allNewsList[key].serviceDivision === SystemConst.SERVICE_DIVISION['CONTENT']) {
                        tmpList.push(state.allNewsList[key]);
                    }
                }
            }

            // stateのnewsListにフィルタ新着情報一覧を設定
            state.newsList = tmpList;

            // データがない場合、一覧に「データはありません。」と表示する
            if (state.newsList.length === 0) {
                state.isNewsLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isNewsLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }
        }

        /**
         * モードを切り替え時のお知らせ一覧、新着情報一覧の再取得・設定
         */
        async function getAndSetList() {

            // 表示データをクリア
            state.infoList = [];
            state.newsList = [];

            // ローディング
            state.isInfoLoading = SystemConst.TABLE_TEXT['LOADING'];
            state.isNewsLoading = SystemConst.TABLE_TEXT['LOADING'];

            // お知らせ、新着一覧情報を取得取得する
            await getList();

            // お知らせ一覧、新着情報一覧を設定する
            setList();

            // お知らせ一覧のタブを「すべて」に戻す
            infoTabsRef.value.internalValue = 0;

            // ローディング削除（お知らせ）
            // データがない場合、「データはありません。」と表示
            if (state.infoList.length === void 0 || state.infoList.length === 0) {
                state.isInfoLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isInfoLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }

            // 「すべて」タブにお知らせ情報を設定する
            changeInfoTabs(0);

            // 新着情報一覧のタブを「すべて」に戻す
            newsTabsRef.value.internalValue = 0;

            // 選択中のタブに新着情報を設定する
            changeNewsTabs(0);

            // ローディング削除（新着情報）
            // データがない場合、「データはありません。」と表示
            if (state.newsList === void 0 || state.newsList.length === 0) {
                state.isNewsLoading = SystemConst.TABLE_TEXT['NO_DATA'];
            } else {
                state.isNewsLoading = SystemConst.TABLE_TEXT['DEFAULT'];
            }
        }

        /**
         * お知らせ一覧画面へ遷移する
         */
        function navigationList() {
            // お知らせ一覧画面へ遷移する
            router.push('/information/list');
        }

        /**
         * お知らせ登録画面に遷移する
         */
        function navigationAdd() {
            router.push('/information/add');
        }

        /**
         * お知らせ一覧添付ファイルダウンロード
         * 
         * @param infoId お知らせID
         * @param seq ファイルシーケンス
         */
        async function informationDownload(infoId: string, seq: number) {
            // お知らせ一覧添付ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/information/{informationId}/{fileSequence}
            // パラメータ：お知らせID
            //            ファイルシーケンス
            const response = await ctx.root.$store.dispatch('information/download',
                {
                    informationId: infoId,
                    fileSequence: seq
                }
            );

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
        }

        /**
         * お知らせ詳細画面に遷移する
         */
        function informationDetail(infoId: string) {
            router.push('/information/detail/' + infoId);
        }

        /**
         * 新着情報詳細画面に遷移する
         */
        async function newsDetail(item: any) {
            // APIで新着情報閲覧テーブルにデータを登録する
            const response = await ctx.root.$store.dispatch('home/browsedNewArrival',
                {
                    newArrivalId: item.newArrivalId // 新着情報ID
                }
            );

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面又はダイアログを表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信が正常の場合
            // state.newsListから選択したデータのリンクURL(linkUrl)に遷移する
            location = item.linkUrl;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state: readonly(state),
            changeInfoTabs,
            changeNewsTabs,
            navigationList,
            navigationAdd,
            informationDownload,
            informationDetail,
            newsDetail,
            onResize,
            commonRef,
            infoTabsRef,
            newsTabsRef,
            isInformation,
            simpleTableHeight,
            tableLoadingText,
            tableNoDataText
        }
    }
});
